/**
 * * 扩展x轴的标题，使其定位保持和轴标签一致，以解决0轴位于图表中间时，轴标题位置跟随0轴的问题
 *
 * - 实现方案：让axisName的y方向的定位保持和axisLabel一致
 *
 * @example
 * ```javascript
 * {
 *   xAxis: {
 *     dvNameLocation: "end",
 *   }
 * }
 * ```
 *
 * TODO:
 * 1、双Y轴的情况
 * 2、X轴放上方的情况
 */
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { type default as CartesianAxisModel } from 'echarts/lib/coord/cartesian/AxisModel';
import GridModel from 'echarts/lib/coord/cartesian/GridModel';
import BoundingRect from 'zrender/lib/core/BoundingRect.js';
import { LayoutRect } from 'echarts/types/src/util/layout';
import { CartesianAxisOption } from 'echarts/types/src/coord/cartesian/AxisModel';
import type GlobalModel from 'echarts/types/src/model/Global';
import { getHeightOfAxisLabel, preRenderAxisName } from './helper';

/**
 * 上一个渲染周期的配置
 * 用于解决配置项的值(比如padding)在每次渲染中错误叠加的问题
 */
const rawPaddingMap = new WeakMap();
/**
 * 为了保证每次渲染时，padding的值不会叠加
 * (如果累加，在拖动dataZoom或者动画下，会导致axis.name的位置偏移越来越大)
 */
const previousPaddingMap = new WeakMap();

function getAxisLabelMargin(axisOption: CartesianAxisOption) {
  /**
   * axisLabel的margin，默认的margin是8，@see https://echarts.apache.org/zh/option.html#xAxis.axisLabel.margin
   */
  const ECHARTS_DEFAULT_AXIS_LABEL_MARGIN = 8;
  return axisOption?.axisLabel?.margin || ECHARTS_DEFAULT_AXIS_LABEL_MARGIN;
}

function getMaxLabelWidth(axisModel: CartesianAxisModel) {
  let maxLabelWidth = -Infinity;
  const labels = axisModel.axis.getViewLabels();
  labels.forEach(label => {
    const labelHeight = getHeightOfAxisLabel(axisModel.option, label.formattedLabel)
      .width as number;
    maxLabelWidth = Math.max(maxLabelWidth, labelHeight);
  });
  return maxLabelWidth;
}

/**
 * 获取0值的坐标值
 * 仅针对数值轴和对数轴生效
 * x轴获取的是0值的X坐标，y轴获取的是0值的Y坐标
 * @see https://echarts.apache.org/zh/option.html#xAxis.type
 * @param axisModel
 */
function getZeroCoord(axisModel: CartesianAxisModel) {
  if (!['value', 'log'].includes(axisModel.option.type)) {
    return null;
  }
  const xAxisExtent = axisModel.axis.scale.getExtent();
  if (xAxisExtent[0] > 0 || xAxisExtent[1] < 0) {
    return null;
  }
  return axisModel.axis.dataToCoord(0);
}
/**
 * @see https://echarts.apache.org/zh/option.html#xAxis.nameTextStyle.padding
 * @param padding
 * @returns
 */
function parsePadding(padding): { top: number; right: number; bottom: number; left: number } {
  if (!padding) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };
  }

  if (typeof padding === 'number') {
    return {
      top: padding,
      right: padding,
      bottom: padding,
      left: padding
    };
  }

  if (Array.isArray(padding)) {
    if (padding.length === 2) {
      return {
        top: padding[0],
        right: padding[1],
        bottom: padding[0],
        left: padding[1]
      };
    }
    if (padding.length === 4) {
      return {
        top: padding[0],
        right: padding[1],
        bottom: padding[2],
        left: padding[3]
      };
    }
    throw new Error('Invalid padding array!');
  }
}

function checkPrerequisitesForX(axisModel: CartesianAxisModel) {
  if (
    !axisModel.get('name') ||
    !['start', 'middle', 'center', 'end'].includes(axisModel.get('dvNameLocation') as string)
  ) {
    return false;
  }
  // 只有start和end两种情况，才会出现0轴定位问题
  return !!['start', 'end'].includes(axisModel.get('dvNameLocation') as string);
}

/**
 * 参考实现 @see https://github.com/apache/echarts/blob/5.3.3/src/component/axis/AxisBuilder.ts#L367
 * 只考虑单X轴的情况，其计算依赖于：
 * 1、左侧Y轴的axisLabel.inside
 * 2、左侧Y轴的label最大宽度
 * 3、Y轴的0刻度是否位于画布中间
 * 4、dvPadding(轴两侧的内边距，这个会影响xAxisExtent的范围)
 */
export function adjustXAxisNameLocation(
  axisModel: CartesianAxisModel,
  gridRectBoundingRect: LayoutRect,
  yAxisModel: CartesianAxisModel,
  gridModel: GridModel
) {
  if (!checkPrerequisitesForX(axisModel)) {
    return;
  }
  // 用dvNameLocation替换nameLocation
  axisModel.option.nameLocation = axisModel.get('dvNameLocation');

  /**
   * align必须配置为 right，使其末尾和绘图区域的右侧边界对齐
   * nameTextStyle.align这个属性=right时，nameTextStyle.padding[3]不会生效，仅nameTextStyle.padding[1]
   */
  axisModel.option.nameTextStyle.align = 'right';

  if (rawPaddingMap.get(axisModel) === undefined) {
    rawPaddingMap.set(axisModel, []);
  }

  if (
    rawPaddingMap.get(axisModel).length === 0 ||
    axisModel.option.nameTextStyle.padding !== previousPaddingMap.get(axisModel)
  ) {
    const rawPadding = parsePadding(axisModel.option.nameTextStyle?.padding);
    rawPaddingMap.set(axisModel, [
      rawPadding.top,
      rawPadding.right,
      rawPadding.bottom,
      rawPadding.left
    ]);
  }

  const labelHeight = getHeightOfAxisLabel(axisModel.option).height;

  let xAxisNameOffset = 0;

  /**
   * Y轴的0刻度在画布内部：需要偏移0轴的Y坐标 + Label的高度
   * 因为当nameTextStyle.verticalAlign=top时，axis.name是相对grid里面的0轴定位的，因此无需偏移gridRectBoundingRect.y
   */
  /**
   * @see https://echarts.apache.org/zh/option.html#xAxis.nameGap
   * 这里的nameGap已经经过预解析了，因此一定有值
   */
  const { nameGap } = axisModel.option as { nameGap: number };
  if (axisModel.option?.dvPadding?.[1]) {
    xAxisNameOffset -= axisModel.option?.dvPadding?.[1] || 0;
  }

  const yAxisZeroYValue = getZeroCoord(yAxisModel);
  const yAxisContainZero = yAxisZeroYValue !== null;

  // 加上parser解析后的配置
  const { top, right, bottom, left } = parsePadding(rawPaddingMap.get(axisModel));

  // Y轴的0刻度在画布内部：需要偏移Label的高度
  let yOffset = 0;
  if (yAxisContainZero) {
    yOffset = yAxisZeroYValue;
  }

  const padding = [yOffset + labelHeight + top, xAxisNameOffset + nameGap + right, bottom, left];

  previousPaddingMap.set(axisModel, padding);
  axisModel.option.nameTextStyle.padding = padding;
}

/**
 * yAxis.name的对齐方式有2种逻辑：
 * 1、当X轴存在0刻度时，跟x轴0刻度对齐
 * 2、当X轴不存在0刻度时，跟x轴左侧第一个刻度对齐
 *
 * 其计算依赖于：
 * 1、同一侧Y轴的axisLabel.inside
 * 2、同一侧Y轴的label最大宽度
 * 3、TODO:X轴的0刻度是否位于画布中间
 * 4、xAxis.boundaryGap的配置
 * @param axisModel
 * @param maxYLabelWidth
 * @returns
 */
export function adjustYAxisNameLocation(
  axisModel: CartesianAxisModel,
  xAxisModel: CartesianAxisModel,
  maxYLabelWidth: number,
  xAxisContainZero: boolean,
  xAxisZeroXValue: number,
  isLeft: boolean
) {
  if (
    !axisModel.get('name') ||
    !['start', 'middle', 'center', 'end'].includes(axisModel.get('dvNameLocation') as string)
  ) {
    return;
  }
  // 用dVNameLocation替换nameLocation
  axisModel.option.nameLocation = axisModel.get('dvNameLocation');

  /**
   * align必须配置为 left，使其左侧和Y轴对齐
   */
  axisModel.option.nameTextStyle.align = 'left';

  const labelMargin = getAxisLabelMargin(axisModel.option);
  maxYLabelWidth += labelMargin;

  // 加上parser解析后的配置
  const { top, right, bottom } = parsePadding(axisModel.option.nameTextStyle?.padding);

  // 当前无数据需要绘制(比如调整了dataZoom的范围)，此时ECharts的机制默认不会显示axisLabel，因此无需考虑axisLabel的宽度
  if (axisModel.axis.scale.isBlank()) {
    maxYLabelWidth = 0;
  }

  /**
   * nameTextStyle.align设置为什么，就只能改这个方向的nameTextStyle.padding
   * left：负数
   * right：正数
   */
  let axisNameLeftPadding = 0;

  // 左侧Y轴
  if (isLeft) {
    if (!axisModel.option?.axisLabel?.inside) {
      axisNameLeftPadding -= maxYLabelWidth;
    }
    // 右侧Y轴
    // eslint-disable-next-line thsjs/elseif-without-else
  } else if (!axisModel.option?.axisLabel?.inside) {
    // TODO:没加padding参数，可能算出来的宽度有偏差
    const { width } = preRenderAxisName(axisModel.option) as unknown as BoundingRect;
    axisNameLeftPadding -= width - maxYLabelWidth;
    console.error('isRight, width = ', width);
  }
  /**
   * @see https://echarts.apache.org/zh/option.html#xAxis.boundaryGap
   * TODO:这个逻辑不应该存在，Y轴不该考虑x轴的0刻度，因为Y轴的name一定是放画布边上的！
   */
  if (xAxisContainZero && xAxisModel.option.boundaryGap) {
    // TODO:仅考虑了类目轴(目前业务中基本都为类目轴，哪怕是日期)，未考虑时间、数值、对数轴情况下，x的0刻度不在最左侧的情况
    // axisNameLeftPadding -= xAxisZeroXValue;
  }
  axisModel.option.nameTextStyle.padding = [top, right, bottom, axisNameLeftPadding];
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('component:beforeupdate', (ecModel: GlobalModel) => {
    let gridRectBoundingRect = null;
    // X轴是否包含0轴
    let xAxisContainZero = false;
    // X轴0值的X值
    let xAxisZeroXValue = 0;
    // 暂时只考虑最多1个X轴的情况
    let xAxisModel = null;
    // 暂时只考虑最多2个Y轴的情况
    const yAxisModel = {
      left: null,
      right: null
    };
    let gridCount = 0;
    let xAxisCount = 0;
    let yAxisCount = 0;

    let gridModel = null;

    ecModel.eachComponent((componentType: string, axisModel) => {
      if (componentType === 'grid') {
        const grid = (axisModel as unknown as GridModel).coordinateSystem;
        const rect = grid.getRect();
        gridRectBoundingRect = rect;
        gridModel = axisModel as unknown as GridModel;
        gridCount++;
      }

      if (componentType === 'xAxis') {
        xAxisModel = axisModel as unknown as CartesianAxisModel;

        xAxisZeroXValue = getZeroCoord(axisModel as unknown as CartesianAxisModel);
        xAxisContainZero = xAxisZeroXValue !== null;
        xAxisCount++;
      }
      if (componentType === 'yAxis') {
        yAxisCount++;
      }
    });
    if (gridCount > 1) {
      console.warn('The dvNameLocation configuration does not currently support multiple grids.');
      return;
    }
    // ifind这边有分组柱状图+背景柱状图的情况，需要支持2个X轴
    if (xAxisCount > 2) {
      console.warn(
        'The dvNameLocation configuration does not currently support cases with more than 2 X - axes.'
      );
      return;
    }
    if (gridCount > 2) {
      console.warn(
        'The dvNameLocation configuration does not currently support cases with more than 2 Y-axes.'
      );
      return;
    }
    ecModel.eachComponent((componentType: string, axisModel) => {
      if (componentType === 'yAxis') {
        const yAxis = axisModel as unknown as CartesianAxisModel;
        /** @see https://echarts.apache.org/zh/option.html#yAxis.position */
        let isLeft = true;
        if (yAxis.option.position === 'left') {
          yAxisModel.left = yAxis;
        } else if (yAxis.option.position === 'right') {
          yAxisModel.right = yAxis;
          isLeft = false;
        } else if (!yAxisModel.left) {
          yAxisModel.left = yAxis;
        } else {
          yAxisModel.right = yAxis;
          isLeft = false;
        }

        // Y轴最长的刻度的宽度
        const maxYLabelWidth = getMaxLabelWidth(yAxis);
        adjustYAxisNameLocation(
          yAxis,
          xAxisModel,
          maxYLabelWidth,
          xAxisContainZero,
          xAxisZeroXValue,
          isLeft
        );
      }
    });

    ecModel.eachComponent((componentType: string, axisModel) => {
      if (componentType === 'xAxis') {
        adjustXAxisNameLocation(
          axisModel as unknown as CartesianAxisModel,
          gridRectBoundingRect,
          yAxisModel.left,
          gridModel
        );
      }
    });
  });
}
